<template>
  <section class="archive-page">
    <header class="archive-header">
      <div>
        <p class="eyebrow">Jaksoarkisto</p>
        <h2>Aiemmat jaksot</h2>
        <p>Valitse jakso ja katso sen lopputulokset sekä erikoistilastot.</p>
      </div>
    </header>

    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <span class="loading-text">Ladataan arkistoa...</span>
    </div>

    <div v-else-if="error" class="error-message">
      <strong>Virhe!</strong>
      <span>{{ error }}</span>
    </div>

    <div v-else-if="periods.length === 0" class="empty-state">
      <p v-if="linkedPeriodMissing" class="empty-text">Linkitettyä jaksoa ei löytynyt arkistosta.</p>
      <p v-else class="empty-text">Arkistoituja jaksoja ei ole vielä saatavilla.</p>
    </div>

    <div v-else class="archive-layout">
      <aside class="period-list" aria-label="Arkistoidut jaksot">
        <button
          v-for="period in periods"
          :key="period.PeriodId"
          class="period-card"
          :class="{ active: selectedPeriodId === period.PeriodId }"
          @click="selectPeriod(period)"
        >
          <span class="period-title">{{ period.Title || formatPeriodRange(period) }}</span>
          <span class="period-winner">Jakson voittaja: {{ period.Winner?.username || 'Ei tietoa' }}</span>
        </button>
      </aside>

      <section class="archive-detail">
        <p v-if="linkedPeriodMissing" class="muted">Linkitettyä jaksoa ei löytynyt arkistosta. Voit valita toisen jakson listasta.</p>
        <div v-if="isDetailLoading" class="detail-loading">
          <div class="loading-spinner small"></div>
          <span>Ladataan jaksoa...</span>
        </div>

        <template v-else-if="selectedArchive">
          <header class="detail-header">
            <div>
              <p class="eyebrow">{{ selectedArchive.PeriodId }}</p>
              <h2>{{ selectedArchive.Title || formatPeriodRange(selectedArchive) }}</h2>
              <p>{{ formatDate(selectedArchive.StartDate) }} - {{ formatDate(selectedArchive.EndDate) }}</p>
            </div>
          </header>

          <div class="podium-grid">
            <article
              v-for="player in podium"
              :key="player.PlayerID || player.rank"
              class="podium-card"
              :class="'rank-' + player.rank"
            >
              <span class="podium-rank">#{{ player.rank }}</span>
              <strong>{{ player.username || 'N/A' }}</strong>
              <span>{{ formatPoints(player.total_points) }}</span>
            </article>
          </div>

          <div class="stats-grid">
            <section
              v-for="section in statSections"
              :key="section.key"
              class="stat-panel"
            >
              <h3>{{ section.title }}</h3>
              <ol v-if="section.rows.length" class="stat-list">
                <li v-for="(row, index) in section.rows" :key="statRowKey(section, row, index)">
                  <span class="stat-rank">{{ index + 1 }}.</span>
                  <span class="stat-name">{{ row.username || 'N/A' }}</span>
                  <span class="stat-value">{{ formatStatValue(row, section) }}</span>
                </li>
              </ol>
              <p v-else class="muted">Ei dataa.</p>
            </section>
          </div>

          <section class="leaderboard-panel">
            <div class="panel-heading">
              <h3>Lopputulokset</h3>
              <span>Top {{ leaderboard.length }}</span>
            </div>
            <div class="table-container">
              <table class="archive-table">
                <thead>
                  <tr>
                    <th>Sija</th>
                    <th>Pelaaja</th>
                    <th>Pisteet</th>
                    <th>Voitot</th>
                    <th>Rahat</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="player in leaderboard" :key="player.PlayerID || player.rank">
                    <td>{{ player.rank }}</td>
                    <td>{{ player.username || 'N/A' }}</td>
                    <td>{{ formatNumber(player.total_points) }} p</td>
                    <td>{{ formatNumber(player.horse_wins) }}</td>
                    <td>{{ formatEuro(player.horse_prize_sum) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </template>
      </section>
    </div>
  </section>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue';
import axios from '../plugins/axios';

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;

export default {
  name: 'PeriodArchivePage',
  props: { requestedStart: { type: String, default: '' } },
  setup(props) {
    const periods = ref([]);
    const selectedPeriodId = ref(null);
    const linkedPeriodMissing = ref(false);
    const selectedArchive = ref(null);
    const isLoading = ref(false);
    const isDetailLoading = ref(false);
    const error = ref(null);

    const podium = computed(() => selectedArchive.value?.Podium || []);
    const leaderboard = computed(() => selectedArchive.value?.LeaderboardTop || []);

    const statSections = computed(() => {
      const stats = selectedArchive.value?.Stats || {};
      return [
        { key: 'horseWins', title: 'Eniten voittoja', suffix: 'voittoa', rows: stats.horseWins || [] },
        { key: 'horsePrizeSum', title: 'Eniten rahaa', suffix: '€', rows: stats.horsePrizeSum || [] },
        { key: 'horseLeadCount', title: 'Eniten keulapaikkoja', suffix: 'kpl', rows: stats.horseLeadCount || [] },
        { key: 'bestDailyScores', title: 'Suurimmat päiväpistepotit', suffix: 'p', rows: stats.bestDailyScores || [] },
      ];
    });

    const formatNumber = (value) => {
      const numberValue = Number(value || 0);
      return new Intl.NumberFormat('fi-FI', { maximumFractionDigits: 1 }).format(numberValue);
    };

    const formatEuro = (value) => `${new Intl.NumberFormat('fi-FI', { maximumFractionDigits: 0 }).format(Number(value || 0))} €`;

    const formatPoints = (value) => `${formatNumber(value)} p`;

    const formatDate = (value) => {
      if (!value) return '';
      const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!match) return String(value);
      return `${Number(match[3])}.${Number(match[2])}.${match[1]}`;
    };

    const formatPeriodRange = (period) => {
      const start = formatDate(period?.StartDate);
      const end = formatDate(period?.EndDate);
      return start && end ? `${start} - ${end}` : 'Jakso';
    };

    const formatStatValue = (row, section) => {
      if (section.key === 'horsePrizeSum') {
        return formatEuro(row.value);
      }
      if (section.key === 'bestDailyScores') {
        return `${formatNumber(row.value)} p ${formatShortDate(row.Date)}`;
      }
      return `${formatNumber(row.value)} ${section.suffix}`;
    };

    const statRowKey = (section, row, index) => `${section.key}-${row.PlayerID || 'row'}-${row.Date || index}`;

    const formatShortDate = (value) => {
      if (!value) return '';
      const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!match) return '';
      return `${Number(match[3])}.${Number(match[2])}.`;
    };

    const fetchDetail = async (periodId) => {
      if (!periodId) return;
      isDetailLoading.value = true;
      error.value = null;
      try {
        const response = await axios.get(`${API_BASE_URL}/periods/${encodeURIComponent(periodId)}`);
        selectedArchive.value = response.data;
      } catch (err) {
        console.error('Error fetching period archive detail:', err.response?.data || err.message);
        error.value = err.response?.data?.error || 'Jakson arkistotietojen lataaminen epäonnistui.';
        selectedArchive.value = null;
      } finally {
        isDetailLoading.value = false;
      }
    };

    const selectPeriod = (period) => {
      if (!period?.PeriodId || period.PeriodId === selectedPeriodId.value) return;
      selectedPeriodId.value = period.PeriodId;
      linkedPeriodMissing.value = false;
      fetchDetail(period.PeriodId);
    };

    const openLinkedPeriod = async () => {
      const target = props.requestedStart
        ? periods.value.find(period => String(period.StartDate).slice(0, 10) === props.requestedStart)
        : periods.value[0];
      linkedPeriodMissing.value = Boolean(props.requestedStart && !target);
      selectedPeriodId.value = target?.PeriodId || null;
      selectedArchive.value = null;
      if (target) await fetchDetail(target.PeriodId);
    };

    const fetchArchives = async () => {
      isLoading.value = true;
      error.value = null;
      try {
        periods.value = [];
        let nextToken = null;
        const seenTokens = new Set();
        do {
          const query = nextToken ? `&next_token=${encodeURIComponent(nextToken)}` : '';
          const response = await axios.get(`${API_BASE_URL}/periods?limit=20${query}`);
          const items = Array.isArray(response.data?.items) ? response.data.items : [];
          periods.value.push(...items);
          nextToken = response.data?.next_token || null;
          if (nextToken && seenTokens.has(nextToken)) throw new Error('Archive pagination did not advance');
          if (nextToken) seenTokens.add(nextToken);
        } while (nextToken);
        await openLinkedPeriod();
      } catch (err) {
        console.error('Error fetching period archives:', err.response?.data || err.message);
        error.value = err.response?.data?.error || 'Arkiston lataaminen epäonnistui.';
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(fetchArchives);
    watch(() => props.requestedStart, () => { if (!isLoading.value) openLinkedPeriod(); });

    return {
      periods,
      selectedPeriodId,
      linkedPeriodMissing,
      selectedArchive,
      podium,
      leaderboard,
      statSections,
      isLoading,
      isDetailLoading,
      error,
      selectPeriod,
      formatDate,
      formatPeriodRange,
      formatNumber,
      formatEuro,
      formatPoints,
      formatStatValue,
      statRowKey,
    };
  },
};
</script>

<style scoped>
.archive-page {
  width: 100%;
}

.archive-header {
  padding: 22px 24px;
  margin-bottom: 18px;
  background: rgba(17, 34, 64, 0.78);
  border: 1px solid #1e3a5f;
  border-radius: 14px;
}

.archive-header h2,
.detail-header h2 {
  margin: 0;
  color: #e6f1ff;
}

.archive-header p:last-child,
.detail-header p:last-child {
  margin: 8px 0 0;
  color: #a8b2d1;
}

.eyebrow {
  margin: 0 0 8px;
  color: #64ffda;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.period-card,
.detail-header,
.podium-card,
.stat-panel,
.leaderboard-panel {
  background: rgba(17, 34, 64, 0.78);
  border: 1px solid rgba(100, 255, 218, 0.16);
  border-radius: 14px;
}

.archive-layout {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 22px;
}

.period-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.period-card {
  width: 100%;
  padding: 16px;
  color: #e6f1ff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, transform 0.2s ease, background-color 0.2s ease;
}

.period-card:hover,
.period-card.active {
  transform: translateY(-1px);
  border-color: #64ffda;
  background: rgba(100, 255, 218, 0.08);
}

.period-title,
.period-winner {
  display: block;
}

.period-title {
  margin-bottom: 8px;
  font-weight: 800;
}

.period-winner {
  color: #64ffda;
  font-size: 0.95rem;
}

.archive-detail {
  min-width: 0;
}

.detail-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 240px;
  color: #a8b2d1;
}

.detail-header {
  padding: 22px;
  margin-bottom: 18px;
}

.podium-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.podium-card {
  padding: 18px;
  min-width: 0;
}

.podium-card.rank-1 {
  border-color: rgba(255, 184, 108, 0.75);
  background: linear-gradient(160deg, rgba(255, 184, 108, 0.18), rgba(17, 34, 64, 0.78));
}

.podium-rank {
  color: #ffb86c;
  font-weight: 800;
}

.podium-card strong,
.podium-card span:last-child {
  display: block;
}

.podium-card strong {
  overflow: hidden;
  margin: 8px 0;
  color: #64ffda;
  font-size: 1.15rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.podium-card span:last-child {
  color: #e6f1ff;
  font-weight: 800;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.stat-panel,
.leaderboard-panel {
  padding: 18px;
}

.stat-panel h3,
.panel-heading h3 {
  margin: 0 0 14px;
  color: #e6f1ff;
  font-size: 1rem;
}

.stat-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0;
  margin: 0;
  list-style: none;
}

.stat-list li {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: baseline;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(100, 255, 218, 0.11);
}

.stat-list li:last-child {
  padding-bottom: 0;
  border-bottom: none;
}

.stat-rank {
  color: #a8b2d1;
  font-weight: 800;
}

.stat-name {
  overflow: hidden;
  color: #64ffda;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stat-value {
  color: #ffb86c;
  font-weight: 800;
  white-space: nowrap;
}

.muted {
  color: #a8b2d1;
}

.panel-heading {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.panel-heading span {
  color: #a8b2d1;
  font-size: 0.9rem;
}

.table-container {
  overflow-x: auto;
}

.archive-table {
  width: 100%;
  min-width: 620px;
  border-collapse: collapse;
  text-align: left;
}

.archive-table th,
.archive-table td {
  padding: 12px 10px;
  border-bottom: 1px solid rgba(100, 255, 218, 0.1);
}

.archive-table th {
  color: #a8b2d1;
  font-size: 0.78rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.archive-table td {
  color: #e6f1ff;
}

.archive-table td:first-child,
.archive-table td:nth-child(3),
.archive-table td:nth-child(4),
.archive-table td:nth-child(5) {
  font-weight: 800;
  white-space: nowrap;
}

.archive-table td:nth-child(3),
.archive-table td:nth-child(5) {
  color: #ffb86c;
}

.loading-container,
.empty-state,
.error-message {
  margin: 30px auto;
}

.loading-spinner.small {
  width: 22px;
  height: 22px;
}

@media (max-width: 900px) {
  .archive-layout {
    grid-template-columns: 1fr;
  }

  .period-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .archive-header,
  .detail-header,
  .stat-panel,
  .leaderboard-panel {
    padding: 16px;
  }

  .podium-grid,
  .stats-grid,
  .period-list {
    grid-template-columns: 1fr;
  }
}
</style>
